import { createFileRoute, redirect } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Users,
  Plus,
  Search,
  Download,
  Upload,
  Edit,
  Trash2,
  Eye,
  Phone,
  Mail,
  MapPin,
  TrendingUp,
  Loader2,
  AlertCircle,
  RefreshCw,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useClients, useDeleteClient } from '@/hooks/queries/useClients';
import { ClientForm } from '@/components/clients/ClientForm';
import { toast } from '@/components/ui/toast/useToast';
import type { Client } from '@lebusiness/types';

export const Route = createFileRoute('/clients/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: ClientsPage,
});

function ClientsPage() {
  const { company } = useAuthStore();
  // ✅ HARDCODED to 'client' type only
  const { data: clients = [], isLoading, error, refetch } = useClients(
    company?.id || '',
    'client'
  );
  const deleteClient = useDeleteClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const filteredClients = clients.filter((client) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      client.name.toLowerCase().includes(searchLower) ||
      client.phone.includes(searchQuery) ||
      (client.email && client.email.toLowerCase().includes(searchLower)) ||
      client.wilaya.toLowerCase().includes(searchLower)
    );
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const stats = {
    total: clients.length,
    totalBalance: clients.reduce((sum, c) => sum + (c.balance || 0), 0),
    totalRevenue: clients.reduce((sum, c) => sum + (c.totalPurchases || 0), 0),
    avgPurchase:
      clients.length > 0
        ? clients.reduce((sum, c) => sum + (c.totalPurchases || 0), 0) / clients.length
        : 0,
  };

  const handleEdit = (client: Client) => {
    setSelectedClient(client);
    setFormOpen(true);
  };

  const handleView = (client: Client) => {
    setSelectedClient(client);
    setViewOpen(true);
  };

  const handleNew = () => {
    setSelectedClient(null);
    setFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (deleteConfirmId === id) {
      try {
        await deleteClient.mutateAsync(id);
        toast.success(
          'تم الحذف بنجاح / Supprimé avec succès',
          'تم حذف العميل / Le client a été supprimé'
        );
        setDeleteConfirmId(null);
      } catch (error) {
        console.error('Failed to delete client:', error);
        toast.error(
          'خطأ / Erreur',
          'فشل حذف العميل / Échec de la suppression'
        );
      }
    } else {
      setDeleteConfirmId(id);
      setTimeout(() => setDeleteConfirmId(null), 3000);
    }
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedClient(null);
  };

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              خطأ في تحميل البيانات / Erreur de chargement
            </h3>
            <p className="text-gray-600">
              {error instanceof Error ? error.message : 'Une erreur est survenue'}
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              العملاء / Clients
            </h1>
            <p className="mt-2 text-gray-600">
              إدارة قاعدة بيانات العملاء / Gérez votre base de clients
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              تحديث / Actualiser
            </Button>
            <Button variant="outline" size="sm">
              <Upload className="w-4 h-4 mr-2" />
              استيراد / Importer
            </Button>
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              تصدير / Exporter
            </Button>
            <Button
              size="sm"
              onClick={handleNew}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              عميل جديد / Nouveau
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">العملاء / Clients</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">الرصيد / Solde total</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {formatCurrency(stats.totalBalance)}
                  </p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">إجمالي المبيعات / CA</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {formatCurrency(stats.totalRevenue)}
                  </p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">متوسط / Moyenne</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {formatCurrency(stats.avgPurchase)}
                  </p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="بحث بالاسم، الهاتف، البريد أو الولاية... / Rechercher par nom, tél, email ou wilaya..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Clients Table */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="border-b">
            <CardTitle>
              قائمة العملاء / Liste des clients ({filteredClients.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        العميل / Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        الاتصال / Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        الولاية / Wilaya
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        NIF / RC
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        الرصيد / Solde
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        إجراءات / Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredClients.map((client) => (
                      <tr
                        key={client.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center">
                              <span className="text-emerald-700 font-semibold">
                                {client.name.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {client.name}
                              </p>
                              {client.address && (
                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {client.address}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <p className="text-sm text-gray-900 flex items-center gap-2">
                              <Phone className="w-4 h-4 text-gray-400" />
                              {client.phone}
                            </p>
                            {client.email && (
                              <p className="text-xs text-gray-500 flex items-center gap-2">
                                <Mail className="w-4 h-4 text-gray-400" />
                                {client.email}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            {client.wilaya}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-xs space-y-1">
                            {client.taxNumber && (
                              <p className="text-gray-600">
                                <span className="font-medium">NIF:</span>{' '}
                                {client.taxNumber}
                              </p>
                            )}
                            {client.commercialRegister && (
                              <p className="text-gray-600">
                                <span className="font-medium">RC:</span>{' '}
                                {client.commercialRegister}
                              </p>
                            )}
                            {!client.taxNumber && !client.commercialRegister && (
                              <p className="text-gray-400">-</p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <p
                              className={`text-sm font-semibold ${
                                (client.balance || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'
                              }`}
                            >
                              {formatCurrency(client.balance || 0)}
                            </p>
                            {client.creditLimit && (
                              <p className="text-xs text-gray-500">
                                Crédit: {formatCurrency(client.creditLimit)}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleView(client)}>
                              <Eye className="w-4 h-4 text-gray-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(client)}
                            >
                              <Edit className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(client.id)}
                              disabled={deleteClient.isPending}
                            >
                              {deleteConfirmId === client.id ? (
                                <span className="text-xs text-red-600 font-medium">
                                  تأكيد؟
                                </span>
                              ) : (
                                <Trash2 className="w-4 h-4 text-red-600" />
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!isLoading && filteredClients.length === 0 && (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  لا يوجد عملاء / Aucun client trouvé
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {clients.length === 0
                    ? 'ابدأ بإضافة عميل جديد / Commencez par ajouter un nouveau client'
                    : 'جرب تغيير معايير البحث / Essayez de modifier vos critères'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Client Form Modal */}
      <ClientForm
        open={formOpen}
        onOpenChange={handleFormClose}
        client={selectedClient}
        onSuccess={() => {
          handleFormClose();
        }}
      />

      {/* Client View Dialog */}
      {viewOpen && selectedClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setViewOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  تفاصيل العميل / Détails du client
                </h2>
                <button
                  onClick={() => setViewOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Info */}
                <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center">
                    <span className="text-emerald-700 font-bold text-2xl">
                      {selectedClient.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{selectedClient.name}</h3>
                    <p className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                      <MapPin className="w-4 h-4" />
                      {selectedClient.wilaya}
                    </p>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-500">الهاتف / Téléphone</p>
                    <p className="text-gray-900 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-emerald-600" />
                      {selectedClient.phone}
                    </p>
                  </div>
                  {selectedClient.email && (
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-gray-500">البريد / Email</p>
                      <p className="text-gray-900 flex items-center gap-2">
                        <Mail className="w-4 h-4 text-emerald-600" />
                        {selectedClient.email}
                      </p>
                    </div>
                  )}
                </div>

                {/* Address */}
                {selectedClient.address && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-500">العنوان / Adresse</p>
                    <p className="text-gray-900">{selectedClient.address}</p>
                  </div>
                )}

                {/* Tax Info */}
                {(selectedClient.taxNumber || selectedClient.commercialRegister) && (
                  <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                    {selectedClient.taxNumber && (
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-gray-500">NIF</p>
                        <p className="text-gray-900 font-mono">{selectedClient.taxNumber}</p>
                      </div>
                    )}
                    {selectedClient.commercialRegister && (
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-gray-500">RC</p>
                        <p className="text-gray-900 font-mono">{selectedClient.commercialRegister}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Financial Info */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-emerald-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-600 mb-1">الرصيد / Solde</p>
                    <p className="text-xl font-bold text-emerald-600">
                      {formatCurrency(selectedClient.balance || 0)}
                    </p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-600 mb-1">إجمالي / Total</p>
                    <p className="text-xl font-bold text-purple-600">
                      {formatCurrency(selectedClient.totalPurchases || 0)}
                    </p>
                  </div>
                  {selectedClient.creditLimit && (
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-600 mb-1">الائتمان / Crédit</p>
                      <p className="text-xl font-bold text-blue-600">
                        {formatCurrency(selectedClient.creditLimit)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    onClick={() => {
                      setViewOpen(false);
                      handleEdit(selectedClient);
                    }}
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    تعديل / Modifier
                  </Button>
                  <Button variant="outline" onClick={() => setViewOpen(false)} className="flex-1">
                    إغلاق / Fermer
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
